import type { ReactNode } from 'react';
import { FloppyDisk, ArrowCounterClockwise, CircleNotch } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

/**
 * Shared save/discard action bar for settings-style edit surfaces.
 *
 * The standard footer for any page/panel with a "dirty draft → Save / Discard"
 * flow (Settings, Org Settings, Sandbox settings, …). The footer renders nothing until
 * there are unsaved changes, then shows a left-aligned hint + right-aligned
 * Discard / Save buttons with a `border-t` divider — integrated into the
 * content, not a detached full-bleed sticky bar.
 *
 * Use this instead of hand-rolling a save footer so every edit surface looks
 * and behaves the same.
 *
 * ── `placement` (FRONT-1263) ─────────────────────────────────────────────
 * `"bottom"` (the default) is the footer described above. `"top"` puts the bar
 * ABOVE the content it governs, as an unsaved-changes banner.
 *
 * THE RULE (FRONT-3305): every Settings and System Settings page uses
 * `"top"`; editors (documents, work items, apps, folders, dialogs) keep the
 * footer, next to the content they save. A settings page with several
 * separately-saved blocks shows ONE top bar through `PageSaveGroup`
 * (`page-save-group.tsx`), never one bar per block. The settings callers are
 * pinned by `settings-save-bar-placement.test.ts`.
 *
 * Top placement exists because the footer was genuinely being MISSED on the
 * Account page's long tab panels. Two reference rules argue for it there: an
 * unsaved draft is an incomplete task, and the documented way to surface one
 * is a banner at the top (`ux-zeigarnik-show-incomplete`); and the bottom of
 * a panel that scrolls is not the "last" position a reader remembers, it is
 * simply off screen (`ux-serial-position`). The rule arguing AGAINST it —
 * `ux-proximity-grouping`, rated higher — is real: a bar far from the field
 * you just edited weakens the link between editing and saving. It loses here
 * only because proximity has ALREADY failed on a panel long enough to hide
 * its own footer.
 *
 * Top placement is STICKY. Relocating the bar without pinning it only moves
 * the blind spot: an edit made in the last section of a long panel then has
 * no save control anywhere in view, which is what the first cut of this
 * shipped.
 *
 * Top placement is ALWAYS MOUNTED (FRONT-2710). A bar that appears on the
 * first edit shoves every field below it down by its own height, under the
 * pointer that just clicked; the earlier 200ms slide-in only turned the jump
 * into a push, and Marko named it: "I hate that stuff". So a clean top bar
 * keeps its frame and reads "All changes saved."; its buttons are only
 * `invisible`, which keeps their size, so going dirty or clean moves nothing.
 * Invisible buttons are out of the accessibility tree, so "Save is visible"
 * still means "the form is dirty" to a test, and `data-unsaved-changes`
 * still marks only a dirty bar.
 */
export interface ActionBarProps {
  /** Are there unsaved changes? The footer hides entirely when false; the top bar stays, with its buttons invisible. */
  dirty: boolean;
  /** Save handler. */
  onSave: () => void;
  /** Discard / revert handler. */
  onDiscard: () => void;
  /** Save request in flight — disables both buttons and shows a spinner. */
  saving?: boolean;
  /** Disable Save independent of dirty/saving (e.g. validation errors). */
  saveDisabled?: boolean;
  /** Save button label. Default: "Save changes". */
  saveLabel?: string;
  /** Discard button label. Default: "Discard" (use "Revert" where it reads better). */
  discardLabel?: string;
  /**
   * Left-side hint. Defaults to "Unsaved changes"; pass a count string (e.g.
   * "2 unsaved changes") or `null` to hide the hint entirely.
   */
  message?: ReactNode;
  /**
   * Where the bar sits relative to the content it saves. Defaults to
   * `"bottom"` — the footer every pre-FRONT-1263 caller renders. See the
   * placement note above before choosing `"top"`.
   */
  placement?: 'bottom' | 'top';
  className?: string;
}

export function ActionBar({
  dirty,
  onSave,
  onDiscard,
  saving = false,
  saveDisabled = false,
  saveLabel = 'Save changes',
  discardLabel = 'Discard',
  message,
  placement = 'bottom',
  className,
}: ActionBarProps) {
  const top = placement === 'top';
  if (!dirty && !top) return null;
  const dirtyMessage = message === undefined ? 'Unsaved changes' : message;
  return (
    <div
      // Named so a test can measure THIS element rather than walking up from
      // a button and landing on the inner button group, which is what the
      // first version of the placement guard did.
      data-testid="action-bar"
      // Set only while dirty, so its presence IS "this box holds an unsaved
      // draft" — the project page refuses to move/reset boxes while one
      // exists (FRONT-2583). A clean top bar stays mounted without it.
      data-unsaved-changes={dirty || undefined}
      data-placement={placement}
      className={cn(
        'px-4 py-3 flex items-center justify-between gap-2',
        // A footer is ATTACHED to the content above it, so a single top
        // hairline is right. A top banner is a standalone element sitting in a
        // column of cards, so it carries a card's own full frame instead — a
        // lone bottom hairline reads as a fragment there.
        top
          // Sticky, and that is the point rather than a flourish: moving the
          // bar to the top without pinning it just relocates the blind spot
          // to the BOTTOM of the panel, where an edit made in the last
          // accordion section then has no save control in view at all. Found
          // by code review after the first cut shipped exactly that. `bg-card`
          // keeps it opaque over the content scrolling beneath it.
          //
          // This is NOT the arrangement the docblock above rejects. What was
          // rejected was a detached, full-bleed `sticky bottom-0` bar floating
          // over the page; this is an in-flow banner inside the content
          // column that pins to the top of the scroll area.
          ? 'sticky top-0 z-10 rounded-lg border border-border bg-card'
          : 'border-t border-border',
        className,
      )}
    >
      {/* The message routinely carries a live count ("2 unsaved changes"),
          so proportional digits reflow the row as it climbs. */}
      <span className="text-[length:var(--text-2xs)] text-muted-foreground tabular-nums">
        {dirty ? dirtyMessage : 'All changes saved.'}
      </span>
      <div className={cn('flex items-center gap-2 transition-[opacity,visibility] duration-150 ease-out', !dirty && 'invisible opacity-0')} aria-hidden={!dirty || undefined}>
        <Button size="sm" variant="ghost" onClick={onDiscard} disabled={saving || !dirty}>
          <ArrowCounterClockwise size={14} className="mr-1" />
          {discardLabel}
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving || saveDisabled || !dirty}>
          {saving ? (
            <CircleNotch size={14} className="animate-spin mr-1" />
          ) : (
            <FloppyDisk size={14} className="mr-1" />
          )}
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
